import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { importCards } from "../firestore";
import { parseDeckImport } from "../importParser";

export default function ImportDeckPage() {
  const { deckId } = useParams();
  const navigate = useNavigate();
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState([]);
  const [errors, setErrors] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [success, setSuccess] = useState("");

  const preview = useMemo(() => rows.slice(0, 20), [rows]);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    setRows([]);
    setErrors([]);
    setSuccess("");
    if (!file) return;

    setParsing(true);
    try {
      const text = await file.text();
      const parsed = parseDeckImport(file.name, text);
      setFileName(file.name);
      setRows(parsed.rows);
      setErrors(parsed.errors || []);
    } catch (err) {
      setErrors([err.message || "Failed to parse file."]);
    } finally {
      setParsing(false);
    }
  };

  const handleImport = async () => {
    if (!rows.length) return;
    setImporting(true);
    setErrors([]);
    setSuccess("");

    try {
      await importCards(deckId, rows);
      setSuccess(`Imported ${rows.length} cards.`);
      setTimeout(() => {
        navigate(`/srs/decks/${deckId}`);
      }, 700);
    } catch (err) {
      setErrors([err.message || "Import failed."]);
    } finally {
      setImporting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Button component={Link} to={`/srs/decks/${deckId}`} sx={{ alignSelf: "flex-start" }}>
          Back to Deck
        </Button>

        <Box>
          <Typography variant="h4" fontWeight={800}>Import Deck</Typography>
          <Typography color="text.secondary">CSV or TSV with columns: front, back, tags?, strict?, accepted?</Typography>
        </Box>

        <Paper sx={{ p: 2 }}>
          <Stack spacing={2}>
            <input type="file" accept=".csv,.tsv,text/csv,text/tab-separated-values,text/plain" onChange={handleFile} />
            {parsing ? <Typography>Parsing file...</Typography> : null}
            {fileName ? <Typography color="text.secondary">Loaded: {fileName}</Typography> : null}
          </Stack>
        </Paper>

        {errors.length > 0 ? <Alert severity="error">{errors.join(" ")}</Alert> : null}
        {success ? <Alert severity="success">{success}</Alert> : null}

        {preview.length > 0 ? (
          <Paper sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h6" fontWeight={700}>Preview (first 20 rows)</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Front</TableCell>
                    <TableCell>Back</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell>Strict</TableCell>
                    <TableCell>Accepted</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.map((row, idx) => (
                    <TableRow key={`${row.front}-${idx}`}>
                      <TableCell>{row.front}</TableCell>
                      <TableCell>{row.back}</TableCell>
                      <TableCell>{row.tags.join(", ")}</TableCell>
                      <TableCell>{row.strict ? "true" : "false"}</TableCell>
                      <TableCell>{row.accepted.join(", ")}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button variant="contained" onClick={handleImport} disabled={importing}>
                {importing ? "Importing..." : `Import ${rows.length} Cards`}
              </Button>
            </Stack>
          </Paper>
        ) : null}
      </Stack>
    </Container>
  );
}
